import endpoints from "@config/endpoints";
import { AncServiceForm } from "@constants/ancConstants";
import { CHILD_API_DETAILS, CHILD_ERROR_MESSAGES } from "@pages/Beneficiary-block/WomenComponent/Child/childConstants";
import { getData, postDataJSON, getDataWithCache, putDataJSON } from "@services/CommonApiService";
import { errorLogger } from "@utils/applicationLogUtils";
import { decodeWithKeyAndShift, encodeWithKeyAndShift } from "@utils/base64Utils";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { CHILD_ERRORS, CHILD_MIN_BIRTH_WEIGHT_GMS } from "@pages/Beneficiary-block/WomenComponent/DeliveryOutcome/constants/DeliveryConstants";
import { normalizeChildUpdatePayload } from '@utils/apiInputValidation';

export const getUserConsentLanguage = async (type: string, consent: string) => {
    try {
        const SESSION_KEY = 'userConsentLanguageCache';
        const cachedData = sessionStorage.getItem(SESSION_KEY);
        let consentCache: Array<{ type: string; consent: string; response: any }> = [];
        if (cachedData) {
            consentCache = JSON?.parse(cachedData) ?? [];
        }
            const cachedItem = consentCache.find(
            item => item.type === type && item.consent === consent
        );
        
        if (cachedItem) {
            return cachedItem.response;
        }
        const res = await getData<any>(`${endpoints.getUserConsentLanguage(type, consent)}`);
        const response = (res && (res.data || res.data.length > 0)) ? res : null;
        if (response) {
            consentCache.push({
                type,
                consent,
                response
            });
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(consentCache));
        }
        return response;
    } catch (error: any) {
        throw new error;
    }
};

export const getChildRegistrationFlows = async () => {
    try {
        const res = await getDataWithCache<any>(`${endpoints.getChildRegistrationFlows}`);
        return (res && (res.data || res.data.length > 0)) ? res : null;
    } catch (error: any) {
        toast.error(AncServiceForm.GET_API_ERROR_MESSAGE(AncServiceForm.REGISTRATION_FLOWS), {
            position: "top-center"
        });
        errorLogger(error?.response?.data || error?.response || error);
    };
};

export const getChildGender = async () => {
    try {
        const res = await getDataWithCache<any>(endpoints.getChildGender);
        return (res && (res.data || res.data.length > 0)) ? res : null;
    } catch (error: any) {
        toast.error(AncServiceForm.GET_API_ERROR_MESSAGE(AncServiceForm.CHILD_GENDER), {
            position: "top-center"
        });
        errorLogger(error?.response?.data || error?.response || error);
    };
};

export const updateChildData = async (payload: any) => {
    try {
        payload = normalizeChildUpdatePayload(payload);
            if (!payload?.childBenfId || !payload?.childName?.trim() || !payload?.genderId || !payload?.maturityStatusId ||
                    !payload?.beneficiaryDob || !dayjs(payload.beneficiaryDob).isValid() || dayjs(payload.beneficiaryDob).isAfter(dayjs()) ||
            (payload.weightAtBirth != null && payload.weightAtBirth < CHILD_MIN_BIRTH_WEIGHT_GMS)) {
                throw new Error(CHILD_ERRORS.UPDATE_REQUIRED_FIELDS);
            }
            payload = { ...payload, childName: payload.childName.trim() };
      const encodedPayload = encodeWithKeyAndShift(JSON.stringify(payload))
      const response = await putDataJSON<any>(`${endpoints.updateChild}`, { data: encodedPayload });
      if (response) {
        toast.success(CHILD_ERROR_MESSAGES.SUCCESS_MSG, {
            position: 'top-center',
        });
        return response;
    }
    } catch (error: any) {
        toast.error(CHILD_ERROR_MESSAGES.POST_API_ERROR_MESSAGE(CHILD_API_DETAILS.UPDATE_CHILD), {
            position: 'top-center',
        });
        errorLogger(error?.response?.data || error?.response || error);
        throw error;
    }
};

export const sendChildOTPViaAadhaar = async (payload: any) => {
    try {
      const encodedPayload = encodeWithKeyAndShift(JSON.stringify(payload));
      const response = await postDataJSON<any>(`${endpoints.sendChildOTPViaAadhaar}`,{ data: encodedPayload });
      if (response) {
        return response;
    }
    } catch (error: any) {
        toast.error(CHILD_ERROR_MESSAGES.POST_API_ERROR_MESSAGE(CHILD_API_DETAILS.SEND_OTP_TO_AADHAR), {
            position: 'top-center',
        });
        errorLogger(error?.response?.data || error?.response || error);
    }
};

export const verifyChildOTPViaAadhaar = async (payload: any) => {
    try {
      const encodedChildPayload = encodeWithKeyAndShift(JSON.stringify(payload));
      const otpResponse = await postDataJSON<any>(`${endpoints.verifyChildOTPViaAadhaar}`, {data: encodedChildPayload});
      if (otpResponse) {
        return otpResponse;
    }
    } catch (error: any) {
        toast.error(CHILD_ERROR_MESSAGES.POST_API_ERROR_MESSAGE(CHILD_API_DETAILS.VERIFY_OTP_TO_AADHAR), {
            position: 'top-center',
        });
        errorLogger(error?.response?.data || error?.response || error);
    }
};


export const getUpdatedToken = async (payload: any) => {
    try {
      const encodedPayload = encodeWithKeyAndShift(JSON.stringify(payload));
      const response = await postDataJSON<any>(`${endpoints.getKycToken}`,{ data: encodedPayload });
      if (response) {
        return response;
    }
    } catch (error: any) {
        toast.error(CHILD_ERROR_MESSAGES.POST_API_ERROR_MESSAGE(CHILD_API_DETAILS.UPDATED_TOKEN), {
            position: 'top-center',
        });
        throw error;
    }
};

export const getChildProfileDetails = async (payload: any) => {
    try {
      const encodedChildPayload = encodeWithKeyAndShift(JSON.stringify(payload));
      const otpResponse = await postDataJSON<any>(`${endpoints.getChildProfileDetails}`, {data: encodedChildPayload});
      if (otpResponse) {
        return otpResponse;
    }
    } catch (error: any) {
        toast.error(CHILD_ERROR_MESSAGES.POST_API_ERROR_MESSAGE(CHILD_API_DETAILS.GET_CHILD_PROFILE), {
            position: 'top-center',
        });
        throw error;
    }
};

/**
 * Fetches WHO Weight-for-Length reference data from the backend API
 * @returns {Promise<any>} - WHO reference data object with boys and girls data
 */
export const fetchWeightForLengthData = async (): Promise<any> => {
  try {
    const response = await getDataWithCache<any>(endpoints.getWeightForLengthData);
    const decodedData = JSON.parse(decodeWithKeyAndShift(response?.data));
    if (decodedData) {
      return decodedData;
    } else {
      return null;
    }
  } catch (error: any) {
      toast.error(CHILD_ERROR_MESSAGES.GET_API_ERROR_MESSAGE(CHILD_API_DETAILS.GET_WEIGHT_FOR_LENGTH_DATA), {
          position: 'top-center',
      });
    return null;
  }
};
